import React, { useCallback, useMemo, useState } from 'react';
import { Card, Col } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { MultiValue } from 'react-select';
import ServerError from '../../../../components/ServerError';
import { SkeletonTable } from '../../components/Skeleton/Table';
import { useNamespacedTranslation } from '../../../../hooks/useNamespacedTranslation';
import { FlairMultiSelectDropdown } from '../../../../atomic/molecules/FlairDropdown/FlairMultiSelectDropdown/FlairMultiSelectDropdown';
import { FilterSelectOption } from '../../../../atomic/molecules/FlairDropdown/types';
import { CardHeaderFilter, PageSize } from '../components/CardHeaderFilter';
import {
  useTableContentFiltration,
  useTableContentPageSize,
} from '../components/useTableContent';
import { CompensationReviewsTable } from './components/CompensationReviewsTable';
import { useCompensationReviewList } from './useCompensationReviews';
import { ReviewStatus } from './types';
import './CompensationReviews.css';

type Props = {
  // Builds the detail URL for the current navigation context (People subtab vs
  // standalone) so the same list is reused without duplication.
  buildDetailUrl: (id: string) => string;
};

const INITIAL_PAGE_SIZE: PageSize = 10;

const ALL_STATUSES: ReviewStatus[] = [
  'PREPARING',
  'SUBMITTED',
  'APPROVED',
  'REJECTED',
  'EXECUTED',
  'FAILED',
  'REVERTED',
];

export const CompensationReviewsList: React.FC<Props> = ({
  buildDetailUrl,
}) => {
  const history = useHistory();
  const t = useNamespacedTranslation('compensationReviews');
  const { reviews, loading, error } = useCompensationReviewList();

  const [filter, onFilterChange] = useTableContentFiltration();
  const [pageSize, onPageSizeChange] =
    useTableContentPageSize(INITIAL_PAGE_SIZE);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

  const roleOptions: FilterSelectOption[] = useMemo(
    () => [
      { value: 'REVIEWER', label: t('roles.reviewer') },
      { value: 'APPROVER', label: t('roles.approver') },
    ],
    [t],
  );

  const statusOptions: FilterSelectOption[] = useMemo(
    () =>
      ALL_STATUSES.map((status) => ({
        value: status,
        label: t(`status.${status.toLowerCase()}`),
      })),
    [t],
  );

  const handleRoleChange = useCallback(
    (selected: MultiValue<FilterSelectOption>) =>
      setSelectedRoles(selected.map((o) => o.value)),
    [],
  );

  const handleStatusChange = useCallback(
    (selected: MultiValue<FilterSelectOption>) =>
      setSelectedStatuses(selected.map((o) => o.value)),
    [],
  );

  const filteredReviews = useMemo(
    () =>
      reviews.filter(
        (review) =>
          (selectedRoles.length === 0 ||
            selectedRoles.includes(review.myRole)) &&
          (selectedStatuses.length === 0 ||
            selectedStatuses.includes(review.status)),
      ),
    [reviews, selectedRoles, selectedStatuses],
  );

  if (error) {
    return <ServerError />;
  }
  if (loading) {
    return <SkeletonTable columns={5} rows={5} />;
  }
  if (reviews.length === 0) {
    return (
      <Card>
        <Card.Body>
          <p className="text-muted mb-0">{t('list.empty')}</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeaderFilter
        onFilterChange={onFilterChange}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
        placeholder={t('list.filterPlaceholder')}>
        <Col className="col-auto">
          <FlairMultiSelectDropdown
            options={roleOptions}
            selectedOptions={selectedRoles}
            onChange={handleRoleChange}
            placeholder={t('list.roleFilterPlaceholder')}
            isSearchable={false}
          />
        </Col>
        <Col className="col-auto">
          <FlairMultiSelectDropdown
            options={statusOptions}
            selectedOptions={selectedStatuses}
            onChange={handleStatusChange}
            placeholder={t('list.statusFilterPlaceholder')}
            isSearchable={false}
          />
        </Col>
      </CardHeaderFilter>
      <CompensationReviewsTable
        reviews={filteredReviews}
        filter={filter}
        pageSize={pageSize}
        onRowClick={(id) => history.push(buildDetailUrl(id))}
      />
    </Card>
  );
};
